import React, { useState } from "react";
import { useNavigate, Routes, Route } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { brandsService } from "../../services/brands";
import { useDeleteBrandMutation } from "../../redux/api/brandsApi";
import { setCurrentBrand } from "../../redux/brand/brandSlice";
import { trackEvent } from "../../utils/tracking";
import BrandPopulatingModal from "./BrandPopulatingModal";

const Settings = ({ brand, brandHash: brandHashProp, onRefetchBrand }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { brands } = useSelector((state) => state.brand);
  const hash = brandHashProp || brand?.hash;
  const [deleteBrand, { isLoading: isDeletingBrand }] =
    useDeleteBrandMutation();
  const [isSyncing, setIsSyncing] = useState(false);

  const handleSync = () => {
    const confirmed = window.confirm(
      "Are you sure you want to resync? This will re-fetch social and website data and overwrite some of the brand profile fields (description, industry, tagline, etc.) with fresh data.",
    );
    if (!confirmed) return;
    setIsSyncing(true);
    brandsService.syncBrand(
      hash,
      () => {
        setIsSyncing(false);
        trackEvent("sync_brand", { brand_hash: hash });
        navigate("/brand/settings/populating/");
      },
      (error) => {
        console.error("Failed to sync brand:", error);
        alert("Failed to resync brand. Please try again.");
        setIsSyncing(false);
      },
    );
  };

  const handleDelete = async () => {
    const brandName = brand?.name || "this brand";
    if (
      !window.confirm(
        `Are you sure you want to delete "${brandName}"? This action cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      await deleteBrand({
        hash,
        projectHash: brand.project_hash,
      }).unwrap();
      const remaining = brands.filter((item) => item.hash !== hash);
      dispatch(setCurrentBrand(remaining[0] || null));
      navigate("/ideas/");
    } catch (error) {
      console.error("Failed to delete brand:", error);
      alert("Failed to delete brand. Please try again.");
    }
  };

  if (!brand) return null;

  return (
    <>
      <div className="max-w-4xl space-y-6">
        <div className="text-theme-text">
          <h2 className="text-xl font-semibold mb-2">Settings</h2>
          <p className="text-sm text-theme-text opacity-70 mb-6">
            Manage sync and destructive actions for {brand.name || "this brand"}
            .
          </p>
        </div>

        <div className="bg-theme-secondary rounded-lg border border-theme-secondary p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-theme-text mb-1">
              Resync
            </h3>
            <p className="text-sm text-theme-text opacity-70">
              Re-fetch social and website data and re-run brand analysis.
            </p>
          </div>
          <div className="pt-4 border-t border-theme-secondary">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-base font-medium text-theme-text mb-1">
                  Resync brand data
                </h4>
                <p className="text-sm text-theme-text opacity-70">
                  Re-fetch linked creators and website, then update the brand
                  profile from the collected data.
                </p>
              </div>
              <button
                onClick={handleSync}
                disabled={isSyncing}
                className="px-4 py-2 bg-theme-primary text-theme-button rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSyncing ? "Syncing..." : "Resync"}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-theme-secondary rounded-lg border border-red-500/30 p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-red-500 mb-1">
              Danger Zone
            </h3>
            <p className="text-sm text-theme-text opacity-70">
              Irreversible and destructive actions
            </p>
          </div>
          <div className="pt-4 border-t border-red-500/20">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-base font-medium text-theme-text mb-1">
                  Delete brand
                </h4>
                <p className="text-sm text-theme-text opacity-70">
                  Once you delete this brand, there is no going back. Ideas and
                  scripts linked to it may be affected.
                </p>
              </div>
              <button
                onClick={handleDelete}
                disabled={isDeletingBrand}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeletingBrand ? "Deleting..." : "Delete brand"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Routes>
        <Route
          path="populating"
          element={
            <BrandPopulatingModal
              brandHash={hash}
              onRefetchBrand={onRefetchBrand}
            />
          }
        />
        <Route
          path="populating/"
          element={
            <BrandPopulatingModal
              brandHash={hash}
              onRefetchBrand={onRefetchBrand}
            />
          }
        />
      </Routes>
    </>
  );
};

export default Settings;
